import { useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';
import { fetchServicePreferences, updateServicePreferences } from '@/api';
import { ErrorMessageResponse, ServicePreferences, ServicePreferencesPayload } from '@/types';
import { useSentryMutation, useSentryQuery } from '../utils/sentryQueryWrapper';
import { RESOLVE_SERVICE_QUERY_KEY } from './useResolveService';

const FIVE_MINUTES_MS = 5 * 60 * 1000;

/**
 * QA T1 — THE LANGUAGE IS PART OF THIS RESOURCE'S IDENTITY.
 *
 * The key used to be a bare constant. But `title` and `description` are
 * localized SERVER-side, per request, from the Accept-Language of the call
 * that fetched them — so two different documents share one cache entry, and
 * with a 5-minute stale window a merchant who switched language kept reading
 * the old one. Observed live: an English dashboard listing Arabic service
 * descriptions, on the step whose entire job is explaining what they are
 * buying. The Settings service list had it too.
 *
 * Keying on the language is the fix rather than shortening staleTime or
 * invalidating on switch: a cache entry should be keyed by everything that
 * changes its CONTENT, and staleTime is then free to stay long — switching
 * back finds the other language still cached instead of re-fetching.
 */
export const servicePreferencesQueryKey = (language: string) => [
  'service-preferences',
  language,
];

// The catalogue barely changes, so a 5-minute stale window keeps the
// create-order screen from re-fetching it on every mount.
//
// defaultErrorMessage: '' is silent mode, and it is load-bearing here. This
// query runs on EVERY page — the Sidebar reads it to decide whether to show
// the Service level entry — and every caller already treats "no catalogue" as
// "render nothing" rather than as an error. Without silent mode, a dashboard
// deployed ahead of the backend, or any transient 5xx, would pop a red toast
// on every navigation for every merchant, which is the opposite of the
// inert-on-deploy guarantee this feature ships with.
export const useFetchServicePreferences = (enabled = true) => {
  // `i18n.language` rather than a module-level read: this re-renders the
  // consumer on a language switch, which re-keys the query in the same tick.
  // A module read would be captured once and never see the change.
  const { i18n } = useTranslation();

  return useSentryQuery<ServicePreferences, ErrorMessageResponse>(
    servicePreferencesQueryKey(i18n.language),
    fetchServicePreferences,
    { enabled, staleTime: FIVE_MINUTES_MS, defaultErrorMessage: '' },
  );
};

export const useUpdateServicePreferences = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();

  return useSentryMutation<ServicePreferences, ErrorMessageResponse, ServicePreferencesPayload>(
    updateServicePreferences,
    {
      defaultErrorMessage: t('operation_failed_please_try_again'),
      // The PUT echoes the persisted state, so seed the cache from it rather
      // than invalidating and paying for an immediate round trip.
      // Seeded under the language it was fetched in — writing it to a bare
      // key would resurrect exactly the shared-entry bug this key fixes.
      onSuccess: (data) => {
        queryClient.setQueryData(servicePreferencesQueryKey(i18n.language), data);
        // 🔴 THE RESOLUTION MUST DIE WITH IT. BOXY-222 issue 1, dashboard half.
        //
        // Seeding the catalogue is right, but the wizard does not skip on the
        // catalogue — it skips on `service-resolution`, which is keyed by
        // PROVINCE and was computed from the preference that just changed.
        // Leave it and the answer from before the edit is reused as if it were
        // fresh: set a default, switch to "choose for each order", and the
        // cached `account_default` still reads as decided, so the step stays
        // skipped. Reported on mobile first; the same hole is here.
        //
        // Invalidated across EVERY province key, not just the current one —
        // one edit can change the answer for provinces the merchant is not
        // looking at, and the next order may be to any of them.
        queryClient.invalidateQueries([RESOLVE_SERVICE_QUERY_KEY]);
      },
    },
  );
};
